import { inject, Injectable } from '@angular/core';

import { Apollo, QueryRef } from 'apollo-angular';
import {
  FilterOptions,
  PropertyMatchBean,
  QuestionContainer
} from '@ui/shared/model';

import { filter, map } from 'rxjs/operators';

import {
  customQuestionsByApplicationQuery,
  propertyApplyProcessPSBeanQuery,
  propertyApplyProcessPSBeansQuery
} from '../queries/property-searcher.queries';
import { PropertyApplyProcessPSBeansResult } from '../../models/property-searcher.model';
import { notificationConfig } from '../../config/notification.config';

@Injectable({
  providedIn: 'root'
})
export class PropertyMatchesFacade {
  private apollo = inject(Apollo);

  private propertyMatchesQuery: QueryRef<PropertyApplyProcessPSBeansResult>;
  private propertyMatchQuery: QueryRef<{
    propertyApplyProcessPSBean: PropertyMatchBean;
  }>;

  public loadPropertyMatchesBeans(filterOptions: FilterOptions) {
    this.propertyMatchesQuery =
      this.apollo.watchQuery<PropertyApplyProcessPSBeansResult>({
        query: propertyApplyProcessPSBeansQuery,
        variables: { filter: filterOptions },
        fetchPolicy: 'no-cache'
      });

    return this.propertyMatchesQuery.valueChanges.pipe(
      filter(response => response.dataState === 'complete'),
      map(response => {
        const propertyApplyProcessPSBeans =
          response.data.propertyApplyProcessPSBeans;

        if (!propertyApplyProcessPSBeans) {
          throw new Error(
            notificationConfig.propertyApplyProcessPSBeans.load.error
          );
        }

        return propertyApplyProcessPSBeans;
      })
    );
  }

  public loadPropertyMatchBean(id: string) {
    this.propertyMatchQuery = this.apollo.watchQuery<{
      propertyApplyProcessPSBean: PropertyMatchBean;
    }>({
      query: propertyApplyProcessPSBeanQuery,
      variables: { id },
      fetchPolicy: 'no-cache'
    });

    return this.propertyMatchQuery.valueChanges.pipe(
      filter(response => response.dataState === 'complete' || !!response.error),
      map(response => {
        if (response.error) {
          throw response.error as Error;
        }

        const propertyApplyProcessPSBean = response?.data
          ?.propertyApplyProcessPSBean as PropertyMatchBean | undefined;

        if (!propertyApplyProcessPSBean) {
          throw new Error(
            notificationConfig.propertyApplyProcessPSBeans.load.error
          );
        }

        return propertyApplyProcessPSBean;
      })
    );
  }

  public loadPropertyMatchBeanQuestions(id: string) {
    return this.apollo.query<{
      customQuestionsByApplication: QuestionContainer;
    }>({
      query: customQuestionsByApplicationQuery,
      variables: { id },
      fetchPolicy: 'no-cache'
    });
  }

  public refetchMatch(id?: string): void {
    if (this.propertyMatchQuery) {
      if (id) {
        void this.propertyMatchQuery.refetch({ id });
      } else {
        void this.propertyMatchQuery.refetch();
      }
    }
  }

  public refetchMatches(): void {
    if (this.propertyMatchesQuery) {
      void this.propertyMatchesQuery.refetch();
    }
  }
}
